"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _immutabilityHelper = _interopRequireDefault(require("immutability-helper"));
var _FormContext = require("./FormContext");
var _FormGroup2 = require("./FormGroup.validation");
var _appendCss = _interopRequireDefault(require("../../utils/appendCss"));
var _FormGroup_css = _interopRequireDefault(require("./FormGroup_css"));
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * FormGroup Component: Handles data from form elements, including changes, validation, and submission
 * Form elements used with FormGroup component must use the registerFormComponent HOC (see ./FormContext.js)
 *
 * @export
 * @class FormGroup
 * @extends {Component}
 */
class FormGroup extends _react.Component {
  constructor(props) {
    var _this;
    super(props);
    _this = this;
    /**
     * Registers DOM nodes to which shaking animation is
     * applied when there is an error in validation for a
     * particular form element
     *
     * @param {array} nodes: DOM nodes to apply shaking animation
     * @param {number} ID: Unique identifier for form element
     */
    (0, _defineProperty2.default)(this, "registerDOMNodes", (nodes, ID) => {
      const {
        DOMNodes
      } = this.state;
      if (!DOMNodes[ID]) {
        this.setState(previousState => ({
          DOMNodes: (0, _immutabilityHelper.default)(previousState.DOMNodes, {
            $merge: {
              [ID]: nodes
            }
          })
        }));
      }
    });
    /**
     * Unregisters DOMNodes that no longer exist.
     *
     * @param {number} ID: Unique identifier for form element
     */
    (0, _defineProperty2.default)(this, "unregisterDOMNodes", ID => {
      const {
        DOMNodes
      } = this.state;
      if (DOMNodes[ID]) {
        this.setState(previousState => ({
          DOMNodes: (0, _immutabilityHelper.default)(previousState.DOMNodes, {
            $unset: [ID]
          })
        }));
      }
    });
    /**
     * Registers form element in FormGroup state at index specified
     * by ID, including its current value, error status, unique identifier,
     * and any validation tests.
     *
     * @param {class} element
     * @param {any} initialValue
     *
     * @returns {number} ID: unique identifier of form element
     */
    (0, _defineProperty2.default)(this, "registerFormElement", (element, initialValue) => {
      const ID = this.globalIndex++;

      //initial registering of form child component.
      const formElementData = {
        value: initialValue,
        validate: "validate" in element.props ? element.props.validate : false,
        isRequired: !!element.props.isRequired,
        isUntouched: true,
        isError: false,
        isApplyingError: false,
        validationMet: false,
        setValidationMet: !!element.props.showValidationMet,
        ID
      };

      //updates state to include new form element data at index of ID
      this.setState(_ref => {
        let {
          data
        } = _ref;
        return {
          data: (0, _immutabilityHelper.default)(data, {
            $merge: {
              [ID]: formElementData
            }
          })
        };
      });
      return ID;
    });
    /**
     * Unregisters a given form element, removing it from validation requirements.
     *
     * @param ID: unique identifier of form element
     */
    (0, _defineProperty2.default)(this, "unregisterFormElement", ID => {
      this.setState(_ref2 => {
        let {
          data
        } = _ref2;
        return {
          data: (0, _immutabilityHelper.default)(data, {
            $unset: [ID]
          })
        };
      });
    });
    /**
     * Universal change event handler for all form elements. This function should
     * be called whenever a change is made to the current value of any registered
     * form element. See registerFormComponent in ./FormContext for more info.
     *
     * If shouldApplyError is true and an error exists with the new value, then apply
     * error animation. If not, then remove form-error class from element's classlist.
     *
     * @param {any} value: new value of element
     * @param {bool} shouldApplyError: specifies whether shaking animation should take
     * place if validation fails
     * @param {number} ID: unique identifier for form element
     *
     * @returns {bool} indicates whether there was an error in validation
     */
    (0, _defineProperty2.default)(this, "onChange", function (value) {
      let shouldApplyError = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
      let ID = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : -1;
      if (ID === -1) return;
      const isError = _this.checkError(value, ID);
      const didErrorChange = _this.state.data[ID].isError !== isError;
      const isUntouched = !_this.state.data[ID].isUntouched ? false : value === "";
      _this.setState(previousState => ({
        data: (0, _immutabilityHelper.default)(previousState.data, {
          [ID]: {
            $set: _objectSpread(_objectSpread({}, previousState.data[ID]), {}, {
              value: value,
              isError: shouldApplyError || !isError ? isError : previousState.data[ID].isError,
              isUntouched: isUntouched,
              validationMet: previousState.data[ID].setValidationMet && !isUntouched && value && value.length > 0 && !isError
            })
          }
        })
      }), () => {
        if (isError && shouldApplyError && (didErrorChange || _this.state.isSubmitting)) {
          _this.applyError(_this.state.DOMNodes[ID], ID);
        }
        if (!isError && didErrorChange) {
          if (_this.state.DOMNodes[ID]) {
            for (const DOMNode of _this.state.DOMNodes[ID]) {
              DOMNode === null || DOMNode === void 0 || DOMNode.classList.remove("form-error");
            }
          }
        }
      });
      return isError;
    });
    /**
     * When a form is submittted, this validates all form components
     * in FormGroup and if there are no errors, the submit function
     * provided in props is called.
     *
     * @param {object} event
     */
    (0, _defineProperty2.default)(this, "handleSubmit", event => {
      event.preventDefault();
      this.setState({
        isSubmitting: true
      }, async () => {
        await this.validateAll();
        if (!this.hasError()) {
          if (this.props.onSubmit) {
            this.props.onSubmit(this.form.current, Object.values(this.state.data));
          }
        }
        this.setState({
          isSubmitting: false
        });
      });
    });
    /**
     * Returns validation results for the value of the element with unique
     * identifier of ID. If value is required, then validates that a value
     * exists. If value is not required and contains no validation prop, then
     * no validation occurs.
     *
     * @param {any} value: new value of element
     * @param {number} ID: unique identifier for element
     * @param {bool} ignoreUntouched: indicates whether to ignore validation because
     * user only focused on form element without inputing any values.
     *
     * @returns {bool} indicates whether value is valid or not
     */
    (0, _defineProperty2.default)(this, "checkError", function (value, ID) {
      let ignoreUntouched = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
      const data = _this.state.data[ID];
      const hasEmptyValue = () => {
        return typeof value === "string" && value.trim() === "";
      };
      const handleValidation = () => {
        if (!data.isRequired && hasEmptyValue() && (!data.isUntouched || !ignoreUntouched)) {
          return true;
        }
        let result;
        if (typeof data.validate === "function") {
          result = data.validate(value);
        } else if (typeof data.validate === "string") {
          switch (data.validate) {
            case "email":
              result = (0, _FormGroup2.emailValidator)(value);
              break;
            case "postalcodeAny":
              result = (0, _FormGroup2.postalCodeValidationAny)(value);
              break;
            case "postalcodeGlobal":
              result = (0, _FormGroup2.postalCodeValidationGlobal)(value);
              break;
            case "alphaNumeric":
              result = (0, _FormGroup2.alphaNumericValidator)(value);
              break;
            case "phone":
              result = (0, _FormGroup2.phoneNumberValidator)(value);
              break;
            default:
              result = false;
          }
        }
        return result;
      };
      return data.validate ? !(handleValidation() || ignoreUntouched && hasEmptyValue() && data.isUntouched) : data.isRequired && (!value || hasEmptyValue()) && (!data.isUntouched || !ignoreUntouched);
    });
    /**
     * Checks whether any errors in any registered form element
     *
     * @returns {boolean} indicates whether an error exists anywhere in form
     */
    (0, _defineProperty2.default)(this, "hasError", () => Object.values(this.state.data).some(data => data.isError));
    /**
     * Validates every registered form element, and if there exist any errors,
     * scrolls to location of error
     */
    (0, _defineProperty2.default)(this, "validateAll", () => {
      let goToTop = false;
      const validatedData = (0, _immutabilityHelper.default)(this.state.data, data => {
        Object.keys(data).forEach(ID => {
          data[ID].isError = this.checkError(data[ID].value, ID, false);
          data[ID].isUntouched = false;
        });
        return data;
      });
      this.setState({
        data: validatedData
      }, () => {
        Object.values(this.state.data).forEach(data => {
          if (data.isError && !data.isApplyingError) {
            this.applyError(this.state.DOMNodes[data.ID], data.ID);
            if (!goToTop) {
              const scrollTo = this.state.DOMNodes[data.ID][0];
              if (scrollTo) {
                try {
                  scrollTo.scrollIntoView({
                    behavior: "smooth",
                    block: "center"
                  });
                } catch (error) {
                  // fallback to prevent browser crashing
                  scrollTo.scrollIntoView(false);
                }
              }
              goToTop = true;
            }
          }
        });
      });
    });
    /**
     * Applies shaking animation to specified dom nodes.
     * Prevents animation from running again until after 500ms.
     *
     * @param {array} DOMNodes
     * @param {number} ID
     */
    (0, _defineProperty2.default)(this, "applyError", (DOMNodes, ID) => {
      if (!DOMNodes) return;
      for (const DOMNode of DOMNodes) {
        if (!DOMNode) return;
        if (DOMNode.classList.contains("input-text__elem")) {
          if (DOMNode.classList.contains("error")) {
            DOMNode.classList.remove("error");
            void DOMNode.offsetWidth;
          }
          DOMNode.classList.add("error");
        } else {
          if (DOMNode.classList.contains("form-error")) {
            DOMNode.classList.remove("form-error");
            void DOMNode.offsetWidth;
          }
          DOMNode.classList.add("form-error");
        }
      }
      this.setState(previousState => ({
        data: (0, _immutabilityHelper.default)(previousState.data, {
          [ID]: {
            $set: _objectSpread(_objectSpread({}, previousState.data[ID]), {}, {
              isApplyingError: true
            })
          }
        })
      }), () => {
        setTimeout(() => this.setState(previousState => ({
          data: (0, _immutabilityHelper.default)(previousState.data, {
            [ID]: {
              $set: _objectSpread(_objectSpread({}, previousState.data[ID]), {}, {
                isApplyingError: false
              })
            }
          })
        })), 500);
      });
    });
    (0, _appendCss.default)("arc_frm", _FormGroup_css.default);
    this.state = {
      data: {},
      DOMNodes: {},
      isSubmitting: false,
      functions: {
        onChange: this.onChange,
        registerFormElement: this.registerFormElement,
        registerDOMNodes: this.registerDOMNodes,
        unregisterDOMNodes: this.unregisterDOMNodes,
        unregisterFormElement: this.unregisterFormElement
      }
    };
    this.form = /*#__PURE__*/_react.default.createRef();
    this.globalIndex = 0;
  }
  /**
   * Uses Context API to send current form state to form elements
   */
  render() {
    return /*#__PURE__*/_react.default.createElement("form", {
      className: "form__elem",
      onSubmit: this.handleSubmit,
      noValidate: true,
      ref: this.form
    }, /*#__PURE__*/_react.default.createElement(_FormContext.FormContext.Provider, {
      value: this.state
    }, this.props.children));
  }
}
exports.default = FormGroup;
FormGroup.propTypes = {
  /** Function that is called on form submission */
  onSubmit: _propTypes.default.func
};